import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Typography } from "@/components/atoms";
import { HowPricingWorksButton, HowPricingWorksModal, PricingMethodCard } from "@/components/organisms";
import { RequirePermission } from "@/permissions/RequirePermission";
import { EPermission } from "@/permissions/types";
import { ContractPricingProfiles } from "./ContractPricingProfiles/ContractPricingProfiles";
import { CustomRates } from "./CustomRates";
import { DefaultRates } from "./DefaultRates";

export const TripCost = () => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);

  return (
    <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
      <div className="flex flex-col justify-between gap-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">Choose Your Pricing Method</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Select how you want to calculate trip costs for your customers
          </Typography>
        </div>
        <div>
          <HowPricingWorksButton onClick={() => setOpenModal(true)} />
        </div>
      </div>
      <div className="mt-6 flex flex-col items-center justify-center gap-5 xl:flex-row xl:items-stretch">
        <PricingMethodCard
          title="Default Rates"
          description="Our system's professionally-calibrated pricing that works for most services"
          icon={<Icon isCustom name="speedtest" size={48} className="text-neutral-dark-gray" />}
          onConfigure={() => navigate("default-rates")}
        >
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
              <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Based on industry standards for fair market rates
              </Typography>
            </div>
            <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
              <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Automatically calculate optimal prices for all trips
              </Typography>
            </div>
            <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
              <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Can be adjusted up or down by a set percentage
              </Typography>
            </div>
          </div>
        </PricingMethodCard>
        <RequirePermission permission={EPermission.CustomRatesManage}>
          <PricingMethodCard
            title="Custom Rates"
            description="Create your own pricing structure with specific rates for different distances"
            icon={<Icon name="Setting5" size={48} className="text-neutral-dark-gray" />}
            onConfigure={() => navigate("custom-rates")}
          >
            <div className="flex flex-col gap-y-2">
              <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
                <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Set special prices for different states
                </Typography>
              </div>
              <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
                <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Maximum control over your pricing strategy
                </Typography>
              </div>
              <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
                <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  If no custom rates are configured, system automatically uses
                  <span className="cursor-pointer text-info" onClick={() => navigate("default-rates")}>
                    {" "}
                    Default Rates
                  </span>
                </Typography>
              </div>
            </div>
          </PricingMethodCard>
        </RequirePermission>
        <RequirePermission permission={EPermission.ContractPricingProfileManage}>
          <PricingMethodCard
            title="Contract Pricing Profiles"
            description="Create contract price packages for your specific clients"
            icon={<Icon name="UserOctagon" size={48} className="text-neutral-dark-gray" />}
            onConfigure={() => navigate("contract-pricing-profiles")}
          >
            <div className="flex flex-col gap-y-2">
              <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
                <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Set exact prices for specific locations and states
                </Typography>
              </div>
              <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
                <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Assigned to specific clients, booking types, or vehicles
                </Typography>
              </div>
              <div className="flex flex-row items-center gap-x-2 rounded-lg bg-neutral-surface-gray px-2 py-1.5">
                <Icon className="flex-shrink-0 text-success" name="TickCircle" variant="Bold" size="lg" />
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Overrides kilometer-based pricing when assigned
                </Typography>
              </div>
            </div>
          </PricingMethodCard>
        </RequirePermission>
      </div>
      <HowPricingWorksModal open={openModal} onClose={() => setOpenModal(false)} />
    </div>
  );
};

TripCost.DefaultRates = DefaultRates;
TripCost.CustomRates = CustomRates;
TripCost.ContractPricingProfiles = ContractPricingProfiles;
